<template>
  <div
    class="modal bottomSheet userModels addRateModal"
    id="add_rate_modal"
    tabindex="-1"
    aria-labelledby="userModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <h5 class="modal-title" id="userModalLabel">اضافة تقييم</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <!-- Modal Body -->
        <div class="modal-body">
          <form @submit.prevent="handleSubmit" id="addRateForm">
            <!-- Hidden input for place ID -->
            <input type="hidden" name="add_place_id" v-model="placeId" />

            <!-- Rating Section -->
            <div class="input-group" @mouseenter="handleStarsMouseEnter" @mouseleave="handleStarsMouseLeave">
              <Vue3StarRatings
                v-model="rating"
                v-model:ratingHover="ratingHover"
                :numberOfStars="5"
                :starSize="computedStarSize"
                :disableClick="false"
              />
              <div id="rateCounter"  class="rateCounter ms-2" ref="rateCounter">{{ ratingHover != 0 ? ratingHover : rating }}</div>
              <!-- <div id="rateCounterHover" class="rateCounter ms-2" ref="rateCounterHover">{{ ratingHover }}</div> -->
              <span v-if="errors.rating" class="has-error text-danger w-100">
                {{ errors.rating }}
              </span>
            </div>

            <!-- Review Text -->
            <div class="input-group mb-3">
              <textarea
                name="new_rate_review"
                v-model="review"
                class="form-control"
                placeholder="نص التقييم ( اختياري )"
                rows="5"
              ></textarea>
              
            </div>

            <!-- File Upload & Preview -->
            <div class="input-group mb-3">
              <div class="upload_card">
                <input type="file" multiple @change="handleFileChange" id="add_rate_file" accept="image/*,video/*"  />
                <label for="add_rate_file">
                  <img :src="addImageSrc" alt="image" draggable="false" />
                  <span>صور / فيديو</span>
                </label>

                <span v-if="errors.media" class="has-error text-danger w-100">
                  {{ errors.media }}
                </span>
              </div>
              <div id="image_preview_container" class="preview-card-container">
                <!-- Render existing images -->
                <template v-if="existingImages.length > 0 && existingImages[0] !== ''">
                  <div
                    v-for="(img, index) in existingImages"
                    :key="'existing-' + index"
                    :class="['image-container', 'image-' + placeId + img.replace('.', '')]">

                      <a v-if="isImage(img)" :href="`/uploads/${img}`" :data-fancybox="`image${index}`" >
                        <img :src="`/uploads/${img}`" alt="Preview" />
                      </a>

                    <video
                      v-else
                      controls
                      :src="`/uploads/${img}`"
                      class="video-preview"
                    ></video>

                    <span
                      :data-id="placeId"
                      :data-dl="dl"
                      :data-image="img"
                      class="deleteIcon delete-existing-image"
                      @click="deleteExistingImage(index)"
                    >
                      <img src="/website/assets/img/icons/delete.png" alt="delete" />
                    </span>
                  </div>
                </template>
                <!-- Render new image previews -->
                <template v-for="(img, index) in imagePreviews" :key="'new-' + index">
                  <div class="image-container">
                    
                    <a v-if="img.type === 'image'" :href="img.url" :data-fancybox="`image${index}`" >
                      <img :src="img.url" alt="Preview" class="item-preview"/>
                    </a>

                    <a v-else :href="img.url"  data-type="html5video" :data-fancybox="`image${index}`" class="video-attachment">
                      <video
                        :src="img.url"
                        class="item-preview"
                      ></video>
                    </a>

                    <span class="deleteIcon" @click="removeNewImage(index)">
                      <img src="/website/assets/img/icons/delete.png" alt="delete" />
                    </span>
                  </div>
                </template>
              </div>
            </div>

            <!-- Submit Button -->
            <div class="input-group mb-0">
              <button id="addRateFormBTN" type="submit" class="form-control has-spinner-link" :class="{ 'loading': loading }">
                <div class="button_text">اضافة</div>
              </button>
            </div>
          </form>
        </div>
        <!-- End Modal Body -->
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch } from 'vue'
import Vue3StarRatings from '~/components/Vue3StarRatings.vue'
import { ratingsRepository } from '~/utils/ratingsRepository'
import { useNuxtApp } from '#app'
import Swal from 'sweetalert2'
import { useBootstrapModal } from '@/composables/useBootstrapModal'
import imageCompression from 'browser-image-compression'

// Set up modal close helper
const { closeModal: closeAddRateModal } = useBootstrapModal("add_rate_modal");
const { showAlert } = useGlobalAlert()

// Define emits for the modal
const emit = defineEmits(['review-added'])

// Reactive form fields
const placeId = ref('')
const rating = ref(0)
const ratingHover = ref(0)
const review = ref('')

// Form error messages
const errors = reactive({
  rating: '',
  media: '',
  review: ''
})

// Loading state for form submission
const loading = ref(false)

// For handling file upload and preview
const imagePreviews = ref<Array<{ file: File; url: string; type: 'image' | 'video' }>>([])

const selectedFiles = ref<File[]>([])

// Existing images (if any)
const existingImages = ref<string[]>([])
const dl = ref('default_dl')

// Static asset for the add-image icon
const addImageSrc = '/website/assets/img/icons/add-image.png'


/** Helpers **/
function isImage(filename: string) {
  return /(jpe?g|png|gif|webp)$/i.test(filename)
}

// Reference for the rate counter element
const rateCounter = ref<HTMLElement | null>(null)
// const rateCounterHover = ref<HTMLElement | null>(null)

// Expose a method to update the place ID from the parent component.
defineExpose({
  setPlaceId(id: string) {
    placeId.value = id
  }
})

// Get $fetch and create the ratings repository.
const { $fetch } = useNuxtApp()
const ratingRepo = ratingsRepository($fetch)
// const handleFileChange = async (e: Event) => {
//   const target = e.target as HTMLInputElement
//   if (!target.files) return

//   // Clear previous selections
//   // selectedFiles.value = []
//   // imagePreviews.value = []

//   for (let file of Array.from(target.files)) {
//     // احسب حجم الصورة قبل الضغط
//     const originalSizeKB = (file.size / 1024).toFixed(2)

//     // Define compression options (adjust as needed)
//     const options = {
//       maxSizeMB: 1,               // Maximum size in MB
//       maxWidthOrHeight: 1024,     // Max width or height
//       useWebWorker: true          // Use web worker for better performance
//     }

//     try {
//       // Compress the file
//       const compressedFile = await imageCompression(file, options)

//       // احسب حجم الصورة بعد الضغط
//       const compressedSizeKB = (compressedFile.size / 1024).toFixed(2)

//       // Save the compressed file for later upload
//       selectedFiles.value.push(compressedFile)

//       // Generate a preview for the compressed file
//       const reader = new FileReader()
//       reader.onload = (event: ProgressEvent<FileReader>) => {
//         if (event.target?.result) {
//           imagePreviews.value.push(event.target.result as string)
//         }
//       }
//       reader.readAsDataURL(compressedFile)
//     } catch (error) {
//       console.error("Error compressing image:", error)
//       // In case of an error, fallback to the original file
//       selectedFiles.value.push(file)
//       const reader = new FileReader()
//       reader.onload = (event: ProgressEvent<FileReader>) => {
//         if (event.target?.result) {
//           imagePreviews.value.push(event.target.result as string)
//         }
//       }
//       reader.readAsDataURL(file)
//     }
//   }
// }


/** File handling **/
const handleFileChange = async (e: Event) => {
  const files = (e.target as HTMLInputElement).files
  if (!files) return

  for (const file of Array.from(files)) {
    // Only allow images or videos
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) continue

    // Start with the original file
    let processed = file


    if (file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file)
      const video = document.createElement('video')

      const isValid = await new Promise<boolean>((resolve) => {
        video.preload = 'metadata'
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(url)
          resolve(video.duration <= 120) // 120 ثانية = دقيقتين
        }
        video.onerror = () => resolve(false)
        video.src = url
      })

      if (!isValid) {
        console.warn('الحد الاقصى للفيديو دقيقتين')

        errors.media = 'الحد الاقصى للفيديو دقيقتين';
        continue
      }
    }

    // If it's an image, try compressing
    if (file.type.startsWith('image/')) {
      try {
        processed = await imageCompression(file, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true
        })
      } catch (err) {
        console.warn('Image compression failed, using original file:', err)
      }
    }


    
    // Create object URL for preview **after** compression (or original)
    const url = URL.createObjectURL(processed)

    // Store in state
    selectedFiles.value.push(processed)
    imagePreviews.value.push({
      file: processed,
      url,
      type: file.type.startsWith('image/') ? 'image' : 'video'
    })
  }
}



/**
 * Removes a newly added image.
 */
const removeNewImage = (index: number) => {
  URL.revokeObjectURL(imagePreviews.value[index].url)
  imagePreviews.value.splice(index, 1)
  selectedFiles.value.splice(index, 1)
}

/**
 * Deletes an existing image.
 */
const deleteExistingImage = (index: number) => {
  // TODO: Implement deletion logic (e.g., call an API) then remove the image.
  existingImages.value.splice(index, 1)
}

/**
 * Validates the form and submits the review.
 */
const handleSubmit = async () => {
  // Reset error messages
  loading.value = true;
  errors.rating = ''
  errors.media = ''
  errors.review = ''

  if (rating.value <= 0) {
    loading.value = false;
    errors.rating = 'يرجى اختيار تقييم'
    return
  }
 

  const formData = new FormData()
  formData.append('place_id', placeId.value)
  formData.append('stars', rating.value.toString())
  formData.append('review', review.value)
  selectedFiles.value.forEach((file, index) => {
    formData.append(`images[${index}]`, file)
  })

  try {
    const response = await ratingRepo.store(placeId.value, formData)
    if (response.response.status === true) {
      closeAddRateModal()

      showAlert('تم اضافه التقييم بنجاح', 'success')


      // Reset the form fields
      rating.value = 0
      review.value = ''
      selectedFiles.value = []
      imagePreviews.value = []

      // Emit the event so the parent can refresh reviews
      emit('review-added')

      setTimeout(() => {
        reloadNuxtApp();
      }, 2000);

    } else {
      Swal.fire({
        icon: 'error',
        title: response.response?.message ?? 'حدث خطأ ما يرجي المحاولة مره اخرى',
        showConfirmButton: false,
        timer: 2000
      })
    }
  } catch (error: any) {
    console.error('Failed to store rating', error)
    if (error.data && error.data.errors) {
      const apiErrors = error.data.errors
      errors.rating = apiErrors.stars ? apiErrors.stars.join(', ') : ''
      errors.review = apiErrors.review ? apiErrors.review.join(', ') : ''
    }
  }
  finally {
    loading.value = false;
  }
}

// Hover effects for rateCounter
const handleStarsMouseEnter = () => {
  rateCounter.value?.classList.add('show')
  // rateCounterHover.value?.classList.add('show')
}

const handleStarsMouseLeave = () => {
  // setTimeout(() => {
  //   rateCounter.value?.classList.remove('show')
  // }, 2000);
  // rateCounterHover.value?.classList.remove('show')
}

watch(rating, () => {
  if (rateCounter.value) {
    rateCounter.value.classList.add('show')
    // setTimeout(() => {
    //   rateCounter.value && rateCounter.value.classList.remove('show')
    // }, 2000)
  }
})

watch(ratingHover, () => {
  if (rateCounter.value) {
    rateCounter.value.classList.add('show')
    // setTimeout(() => {
    //   rateCounter.value && rateCounter.value.classList.remove('show')
    // }, 2000)
  }
})

// Use a default screen width (e.g., 1024) for SSR where window is undefined.
const screenWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);

function updateScreenWidth() {
  if (typeof window !== 'undefined') {
    screenWidth.value = window.innerWidth;
  }
}

// Only add event listeners when running in the browser.
if (typeof window !== 'undefined') {
  onMounted(() => {
    updateScreenWidth();
    window.addEventListener('resize', updateScreenWidth);
  });

  onUnmounted(() => {
    updateScreenWidth();
    window.removeEventListener('resize', updateScreenWidth);
  });
}

// Compute star size: if screen width is below 768px, use 40; otherwise, use 55.
const computedStarSize = computed(() => {
  
  return screenWidth.value < 768 ? 10 : 55;
});

</script>
<style scoped>
.modal.bottomSheet {
  /* Customize modal styles as needed */
}

.upload_card {
  position: relative;
  display: inline-block;
}

.upload_card input[type='file'] {
  display: none;
}

.upload_card label {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.preview-card-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  /* margin-top: 1rem; */
}

.image-container {
  position: relative;
}

.image-container img {
  max-width: 100px;
  max-height: 100px;
  object-fit: cover;
}

.deleteIcon {
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;
}

/* Example styling for hover effects on rateCounter */
.rateCounter.show {
  color: #000;
}

.rateCounterHover.show {
  color: #000;
}

</style>
